import { translator } from 'translation/useTranslator';
import { YupTestConfig, YupTestFunction, YupContext } from './yupUtils';

const MIN_LENGTH = 2;
const MAX_LENGTH = 64;

const COUNTRY_VAT_PATTERNS: Record<string, RegExp> = (() => {
  const bodies: Record<string, string> = {
    // EU member states (VIES formats)
    AT: 'U[0-9]{8}',
    BE: '[01][0-9]{9}',
    BG: '[0-9]{9,10}',
    CY: '[0-9]{8}[A-Z]',
    CZ: '[0-9]{8,10}',
    DE: '[0-9]{9}',
    DK: '[0-9]{8}',
    EE: '[0-9]{9}',
    EL: '[0-9]{9}',
    ES: '[0-9A-Z][0-9]{7}[0-9A-Z]',
    FI: '[0-9]{8}',
    FR: '[0-9A-Z]{2}[0-9]{9}',
    HR: '[0-9]{11}',
    HU: '[0-9]{8}',
    IE: '([0-9]{7}[A-Z]{1,2}|[0-9][0-9A-Z+*][0-9]{5}[A-Z])',
    IT: '[0-9]{11}',
    LT: '([0-9]{9}|[0-9]{12})',
    LU: '[0-9]{8}',
    LV: '[0-9]{11}',
    MT: '[0-9]{8}',
    NL: '[0-9]{9}B[0-9]{2}',
    PL: '[0-9]{10}',
    PT: '[0-9]{9}',
    RO: '[0-9]{2,10}',
    SE: '[0-9]{12}',
    SI: '[0-9]{8}',
    SK: '[0-9]{10}',
    // Non-EU schemes
    GB: '([0-9]{9}|[0-9]{12}|GD[0-9]{3}|HA[0-9]{3})',
    EU: '[0-9]{9}',
    US: '[0-9]{9}',
    BR: '[0-9]{14}',
    IN: '[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]',
    AU: '[0-9]{11}',
    JP: '[0-9]{13}'
  };

  // Greece is colloquially "GR" but "EL" in VIES; accept either prefix.
  bodies.GR = bodies.EL;

  return Object.fromEntries(
    Object.entries(bodies).map(([code, body]) => [code, new RegExp(`^${body}$`)])
  );
})();

const isVatValid = (vat: string | undefined | null, initialValue?: string): boolean => {
  if (!vat) {
    return true;
  }

  if (initialValue && initialValue === vat) {
    return true;
  }

  const normalizedVat = vat.replace(/[ .\-/]/g, '').toUpperCase();
  if (normalizedVat.length < MIN_LENGTH || normalizedVat.length > MAX_LENGTH) {
    return false;
  }

  const countryCode = normalizedVat.slice(0, 2);
  const body = normalizedVat.slice(2);
  const pattern = COUNTRY_VAT_PATTERNS[countryCode];

  return pattern !== undefined && pattern.test(body);
};

export default isVatValid;

const vatTestFunction: YupTestFunction = function (this, vat) {
  const context = this.options.context as YupContext;
  const initialValue = context?.[`initial${this.path}`] ?? '';
  return isVatValid(vat, initialValue);
};

export const getVatYupTest = (): YupTestConfig => ({
  name: 'isVatValid',
  message: translator?.vatIsInvalid() ?? '',
  test: vatTestFunction
});
